import {
    Component,
    OnInit,
    Input,
    OnChanges,
    SimpleChanges,
    OnDestroy,
    Output,
    EventEmitter,
} from "@angular/core";
import { UserRoles } from "../../Constants";
import {
    UpsertUserRequest,
    User,
    UserRole,
    Customer,
} from "../../WebApiClient";
import { Guid } from "../../models/Guid";
import { LoginService } from "../../services/login.service";
import { UserService } from "../../services/user.service";
import { CustomerService } from "../../services/customer.service";
import { Observable, Subscription } from "rxjs";
import { Login } from "../../models/Login";
import { Router } from "@angular/router";
import { BlockNavigationIfChange } from "../../guards/block-onchanged.guard";

@Component({
    selector: "app-edit-user",
    templateUrl: "./edit-user.component.html",
    styleUrls: ["./edit-user.component.scss", "../../style/crudFloat.scss"],
    standalone: false,
})
export class EditUserComponent
    implements OnChanges, OnInit, OnDestroy, BlockNavigationIfChange
{
    public readonly roles = UserRoles;
    public user: UpsertUserRequest;
    public customers: Customer[];
    customersSubscription: Subscription;

    private isInputValid: boolean = false;

    constructor(
        customerService: CustomerService,
        private readonly userService: UserService,
        private readonly loginService: LoginService,
        private readonly router: Router
    ) {
        this.customers = customerService.customers;
        this.customersSubscription =
            customerService.customersObservable.subscribe((c) => {
                if (c) this.customers = c;
            });
        this.user = this.newUser();
        this.hasChanges = () => false;
    }
    hasChanges: () => Observable<boolean> | Promise<boolean> | boolean;
    ngOnDestroy(): void {
        this.customersSubscription.unsubscribe();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if ("userToChange" in changes) {
            this.user = this.newUser();
        }
    }

    ngOnInit(): void {
        this.user = this.newUser();
    }

    @Input("user")
    userToChange?: User;

    @Output()
    formIsDirty = new EventEmitter<boolean>();

    get inputValid(): boolean {
        if (!this.user) return false;
        if (!this.user.id) return false;
        if (this.user.id === Guid.empty) return false;

        if (this.user.userRole == UserRole.Customer && !this.user.customerId)
            return false;

        if (!this.isInputValid) {
            return false;
        }
        return true;
    }

    get isUserDeletable(): boolean {
        if (!this.user) return false;
        if (!this.user.id) return false;
        if (this.user.id === Guid.empty) return false;
        if (this.userToChange?.isDeleted) return false;

        const currentUser = this.loginService.currentUser;
        if (currentUser instanceof Login && this.user.id === currentUser.id)
            return false;

        return true;
    }

    get showRestoreButton(): boolean {
        if (!this.user) return false;
        if (!this.user.id) return false;
        if (this.user.id === Guid.empty) return false;
        const currentUser = this.loginService.currentUser;
        if (currentUser instanceof Login && this.user.id === currentUser.id)
            return false;
        return this.userToChange?.isDeleted == true;
    }
    get showDeleteButton(): boolean {
        return !this.showRestoreButton;
    }

    get passwordResetEnabled(): boolean {
        return this.userToChange?.passwordReset == false;
    }

    update() {
        if (!this.isInputValid) return;

        if (this.user.customerId === "") {
            this.user.customerId = undefined;
        }

        if (this.user.resourceId === "") {
            this.user.resourceId = undefined;
        }
        this.formIsDirty.emit(false);

        this.userService.upsertUser(this.user).subscribe(() => {

            const departmentIds = (this.user.departmentIds ?? [])
                .filter((id): id is string => !!id);

            this.userService.setUserDepartments(
                this.user.id!,
                departmentIds
            ).subscribe(() => {
                this.cancel();
                this.router.navigateByUrl('/', { skipLocationChange: true }).then(() => {
                    this.router.navigate(['/users']);
                });
            });
        });
    }

    delete() {
        if (!this.isUserDeletable) return;
        var isSure = confirm("Are you sure you want to delete the user?");
        if (!isSure) return;
        this.userService.deleteUser(this.user!.id!);
    }

    restore() {
        if (!this.userToChange?.isDeleted) return;
        if (!this.user.id) return;
        this.userService.restoreUser(this.user.id);
    }

    passwordReset() {
        if (!this.user.id) return;
        this.loginService.passwordReset(this.user.username).then((c) => {
            if (c && c.success && this.userToChange) {
                this.router.navigateByUrl("/users");
            }
        });
    }

    cancel() {
        this.router.navigateByUrl("/users");
    }

    private newUser(): UpsertUserRequest {
        const result = new UpsertUserRequest();
        result.id = this.userToChange?.id ?? Guid.newGuid();
        result.email = this.userToChange?.email ?? "";
        result.name = this.userToChange?.name ?? "";
        result.password = "";
        result.username = this.userToChange?.username ?? "";
        (<any>result.userRole) = this.userToChange?.userRole ?? "";
        (<any>result.userRoleAdmin) = this.userToChange?.userRoleAdmin ?? "";
        (<any>result.userRoleStorage) =
            this.userToChange?.userRoleStorage ?? "";
        (<any>result.userRoleSustainability) =
            this.userToChange?.userRoleSustainability ?? "";
        (<any>result.userRoleTerminal) =
            this.userToChange?.userRoleTerminal ?? "";
        result.customerId = this.userToChange?.customerId ?? "";
        result.departmentIds = this.userToChange?.departments?.map(d => d.id).filter((id): id is string => !!id) ?? [];
        result.resourceId = this.userToChange?.resourceId ?? "";
        return result;
    }

    formIsValid(isvalid: boolean): void {
        this.isInputValid = isvalid;
    }

    innerFormIsDirty($event: any) {
        this.formIsDirty.emit($event);
    }
}
